import { useMutation } from '@tanstack/react-query'
import { showToaster } from '../../utils/show-toaster.util'
import {
  handlePostRequest,
  handleLogoutRequest,
  handleGetRequest,
} from '../../utils/api-handler.util'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import { setUser } from '@sentry/react'
import { AuthContext } from '../AuthContext'
import { useNavigate } from 'react-router-dom'
import USER_TYPE from '../../constants/userType'

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate()

  const getCurrentUser = () => {
    try {
      const storedUser = localStorage.getItem('user') || ''
      const user = storedUser ? JSON.parse(storedUser) : null

      if (user) {
        setUser({
          username: user.name,
          id: user.id,
        })
      }

      return user
    } catch (error) {
      console.error('Error parsing stored user:', error)
      return null
    }
  }

  const loginMutation = useMutation({
    mutationFn: (credentials) => handlePostRequest('/login', credentials),
    onSuccess: (response) => {
      const userData = {
        ...response.data,
        token: response.data.sessionToken,
        role: response.data.userType,
      }
      localStorage.setItem('user', JSON.stringify(userData))
      // setCurrentUser(userData);
      if (userData) {
        setUser({
          username: userData.name,
          id: userData.id,
        })
      }
      // QueryKeys.authBase.invalidate();
      showToaster('success', 'Login successful')

      return response
    },
    onError: () => {
      localStorage.removeItem('user')
    },
  })

  const refreshUserMutation = useMutation({
    mutationFn: () => handleGetRequest('/me'),
    onSuccess: (response) => {
      const userData = {
        ...response.data,
        token: response.data.sessionToken,
        role: response.data.userType,
      }
      localStorage.setItem('user', JSON.stringify(userData))
      if (userData) {
        setUser({
          username: userData.name,
          id: userData.id,
        })
      }

      return response
    },
  })

  const logoutMutation = useMutation({
    mutationFn: () => handleLogoutRequest(),
    onSuccess: () => {
      QueryKeys.authBase.invalidate()
      QueryKeys.authUser.invalidate()
      setUser(null)
      localStorage.removeItem('user')
      showToaster('success', 'Logged out successfully')
      navigate('/')
    },
  })

  const logout = async () => {
    await logoutMutation.mutateAsync()

    return Promise.resolve()
  }

  const hasRole = (roles) => {
    const currentUser = getCurrentUser()
    if (!currentUser) return false
    if (!roles) return true

    const userRoles = Array.isArray(currentUser.role) ? currentUser.role : [currentUser.role]

    return Array.isArray(roles)
      ? roles.some((role) => userRoles.includes(role))
      : userRoles.includes(roles)
  }

  const isAuthenticated = () => {
    const currentUser = localStorage.getItem('user')

    return !!currentUser
  }

  const user = getCurrentUser()

  const handleRefresh = async () => {
    if (logoutMutation.isPending) return
    if (!isAuthenticated()) return

    await refreshUserMutation.mutateAsync()
  }

  const value = {
    currentUser: user,
    userType: user?.userType || USER_TYPE.guest,
    activeSubscription: user?.activeSubscription || null,
    logout,
    hasRole,
    isAuthenticated,
    loading: logoutMutation.isPending,
    login: loginMutation.mutateAsync,
    refresh: handleRefresh,
  }

  return (
    <AuthContext.Provider value={value}>
      {!logoutMutation.isPending && children}
    </AuthContext.Provider>
  )
}
